import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { AdminBaseEditComponent } from 'src/modules/sm-base/components/admin-base-edit/admin-base-edit.component';
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendFieldListItem } from 'src/modules/sm-base/netgen/FrontendFieldListItem';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { FrontendForm } from 'src/modules/sm-base/netgen/FrontendForm';
import { TableCellType } from 'src/modules/sm-base/netgen/TableCellType';
import { TableColumn } from 'src/modules/sm-base/netgen/TableColumn';
import { Objects } from 'src/modules/utils/shared/objects';
import { ProjectPlanningTools } from '../../models/project-planning-tools.model';
import { ProjectCustomFieldDefinition } from '../../netgen/ProjectCustomFieldDefinition';
import { ProjectTemplate } from '../../netgen/ProjectTemplate';
import { ProjectTemplateField } from '../../netgen/ProjectTemplateField';
import { ProjectType } from '../../netgen/ProjectType';
import { ProjectPlanningService } from '../../services/project-planning.service';

@Component({
    selector: 'app-edit-project-type',
    templateUrl: '../../../sm-base/components/admin-base-edit/admin-base-edit.component.html',
    styleUrl: '../../../sm-base/components/admin-base-edit/admin-base-edit.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditProjectTemplateComponent extends AdminBaseEditComponent<ProjectTemplate> {

    service = inject(ProjectPlanningService);

    definition = ProjectPlanningTools.definitionProjectTemplate;

    projectTypes: ProjectType[];
    projectFields: ProjectCustomFieldDefinition[];
    
    getForm(): FrontendForm {
        return this.app.tForm("entity.ProjectTemplate.fields", new FrontendForm([
            new FrontendField("title", "", FrontendFieldType.string, { 
                mandatory: true,
                guiGroup: "title",
                guiWidthInGroup: 65
            }),
            new FrontendField("typeId", "", FrontendFieldType.dropdown, { 
                mandatory: true,
                dropdownCanFilter: true, 
                guiGroup: "title", 
                guiWidthInGroup: 35,
                listItems: FrontendFieldListItem.make(this.projectTypes, item => [item.id, item.title])
            }),
            new FrontendField("_fields", this.app.t("entity.ProjectTemplate.other.customFields"), FrontendFieldType.table, {
                tableColumns: [
                    new TableColumn("use", this.app.t("entity.ProjectTemplate.other.useField"), TableCellType.bool, 1, {
                        editable: true
                    }),
                    new TableColumn("key", this.app.t("entity.ProjectTemplate.other.field"), TableCellType.string, 30),
                    new TableColumn("value", this.app.t("entity.ProjectTemplateField.fields.value"), TableCellType.string, 30, {
                        editable: true,
                        editableCallback: row => row.row.raw.use as boolean,
                        editor: params => {
                            let def = params.row.raw.field as ProjectCustomFieldDefinition;
                            if (def?.dropdownField) {
                                return new FrontendField("field", this.app.t("entity.ProjectTemplate.other.field"), FrontendFieldType.dropdown, {
                                    listItems: FrontendFieldListItem.make(def.dropdownOptions)
                                });
                            }
                            else {
                                return null;
                            }
                        },
                        getStyleClassCallback: row =>
                                        (row.row.raw.use as boolean)
                                            ? "" : "xbg-gray-200"
                    })
                ],
                tableCanAddRow: false,
                tableCanRemoveRow: false
            }),
            new FrontendField("description", "", FrontendFieldType.string, { mandatory: true })                               
        ]));
    }
    
    async initAdditionalData(): Promise<void> {
        this.projectTypes = await this.service.getProjectTypes();
        this.projectFields = await this.service.getProjectCustomFieldDefinitions();
    }
    
    async fillForm(): Promise<void> {
        await super.fillForm();
        this.form.setValue("_fields", this.projectFields.map(field => ({
            use: this.isInsert() || this.item.fields?.some(f => f.fieldDefinitionId == field.id),
            key: field.title,
            value: this.item.fields?.find(f => f.fieldDefinitionId == field.id)?.value ?? "",
            field
        })));
    }

    async fromForm(): Promise<void> {
        await super.fromForm();
        let fields: ProjectTemplateField[] = [];
        for (let row of this.form.getValue("_fields") as any[]) {
            if (row.use) {
                let id = (row.field as ProjectCustomFieldDefinition).id;
                let field = this.item.fields?.find(f => f.fieldDefinitionId == id);
                if (field == null) {
                    field = Objects.fromPlain(ProjectTemplateField, {
                        fieldDefinitionId: id
                    });
                }
                field.value = row.value;
                fields.push(field);
            }
        }
        this.item.fields = fields;
    }
    
}
